import api, { setAccessToken } from './client';

/**
 * Org-level roles plus the platform-only SUPER_ADMIN. Mirrors the backend
 * Prisma enum — SUPER_ADMIN is never assignable through any org UI, it only
 * ever arrives on an already-provisioned user (see FRONTEND_RBAC_GUIDE.md §6).
 */
export type UserRole =
  | 'SUPER_ADMIN'
  | 'ORGANIZATION_OWNER'
  | 'ADMIN'
  | 'LEAD_MANAGER'
  | 'MEMBER'
  /** Website demo (trial) sign-up — see usePermissions.ts for its UI scope. */
  | 'CUSTOMER';

export type UserStatus = 'ACTIVE' | 'DISABLED';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  mustChangePassword: boolean;
  /** null only for SUPER_ADMIN, who isn't tied to an organization. */
  organizationId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  /** Mirrors user.mustChangePassword; login returns it at the top level too. */
  mustChangePassword?: boolean;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  workspace?: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface RegisterPayload {
  email: string;
  password: string;
  name?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  /** Minimum 8 characters — enforced by the backend DTO as well. */
  newPassword: string;
}

export const register = async (payload: RegisterPayload): Promise<AuthResponse> => {
  const { data } = await api.post<AuthResponse>('/auth/register', payload);
  setAccessToken(data.accessToken);
  return data;
};

export const login = async (payload: LoginPayload): Promise<AuthResponse> => {
  const { data } = await api.post<AuthResponse>('/auth/login', payload);
  setAccessToken(data.accessToken);
  return data;
};

export const refreshSession = async (): Promise<AuthResponse> => {
  // Refresh uses httpOnly cookie (withCredentials: true)
  // Send empty body - backend will read refresh token from cookie
  const { data } = await api.post<AuthResponse>('/auth/refresh', {});
  setAccessToken(data.accessToken);
  return data;
};

/** Changes your own password; also clears mustChangePassword server-side. */
export const changePassword = async (
  payload: ChangePasswordPayload,
): Promise<{ success: boolean }> => {
  const { data } = await api.post<{ success: boolean }>('/auth/change-password', payload);
  return data;
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/auth/logout');
  } finally {
    setAccessToken(null);
  }
};
